import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  Copy, ArrowDownToLine, ArrowUpToLine, ShieldCheck, CheckCircle2,
  XCircle, Wallet, Coins, ArrowRight, Clock, Info, RefreshCw,
  AlertTriangle, ExternalLink, Zap, MessageSquarePlus, Send, FileText, CreditCard,
} from 'lucide-react';
import { walletAPI, transactionAPI, depositAPI, gamefuelAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DepositModal } from '@/components/custom/DepositModal';
import { CardPurchaseModal } from '@/components/custom/CardPurchaseModal';

export default function WalletPage() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositAsset, setDepositAsset] = useState('trx');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);
  const [withdrawAsset, setWithdrawAsset] = useState('usdt');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [txFilter, setTxFilter] = useState('all');
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [cardPurchaseOpen, setCardPurchaseOpen] = useState(false);
  const [gamefuelStatus, setGamefuelStatus] = useState(null);

  // On-chain deposit state
  const [platformAddress, setPlatformAddress] = useState('');
  const [myDeposits, setMyDeposits] = useState([]);
  const [recheckingId, setRecheckingId] = useState(null);

  // Manual TX submit
  const [manualAsset, setManualAsset] = useState('usdt');
  const [manualAmount, setManualAmount] = useState('');
  const [manualTxHash, setManualTxHash] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [manualLoading, setManualLoading] = useState(false);

  // Support form
  const [supportTxHash, setSupportTxHash] = useState('');
  const [supportSenderAddr, setSupportSenderAddr] = useState('');
  const [supportAmount, setSupportAmount] = useState('');
  const [supportAsset, setSupportAsset] = useState('usdt');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);
  const [myTickets, setMyTickets] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [walletRes, txRes, eligRes, addrRes, depRes, ticketRes, gfRes] = await Promise.all([
        walletAPI.getWallet(),
        transactionAPI.getTransactions({ limit: 50, entry_type: txFilter === 'all' ? '' : txFilter }),
        walletAPI.getWithdrawalEligibility(),
        depositAPI.getPlatformAddress().catch(() => ({ data: {} })),
        depositAPI.getMyDeposits({ limit: 30 }).catch(() => ({ data: { deposits: [] } })),
        depositAPI.getMyTickets().catch(() => ({ data: { tickets: [] } })),
        gamefuelAPI.getStatus().catch(() => ({ data: null })),
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data.transactions);
      setEligibility(eligRes.data);
      setPlatformAddress(addrRes.data?.platform_address || '');
      setMyDeposits(depRes.data?.deposits || []);
      setMyTickets(ticketRes.data?.tickets || []);
      setGamefuelStatus(gfRes.data);
    } catch (err) {
      console.error('Wallet load error:', err);
    } finally {
      setLoading(false);
    }
  }, [txFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  // Demo deposit
  const handleDeposit = async () => {
    if (!depositAmount || Number(depositAmount) <= 0) { toast.error(t('wallet.validAmount')); return; }
    setDepositLoading(true);
    try {
      await walletAPI.simulateDeposit({ asset: depositAsset, amount: Number(depositAmount) });
      toast.success(`${depositAmount} ${depositAsset.toUpperCase()} ${t('wallet.depositedSuccess')}`);
      setDepositAmount('');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('wallet.depositFailed'));
    } finally {
      setDepositLoading(false);
    }
  };

  // Manual TX hash submit
  const handleManualSubmit = async () => {
    if (!manualTxHash || manualTxHash.length < 20) {
      toast.error(t('wallet.enterValidTxHash'));
      return;
    }
    if (!manualAmount || Number(manualAmount) <= 0) {
      toast.error(t('wallet.validAmount'));
      return;
    }
    setManualLoading(true);
    try {
      await depositAPI.submit({
        tx_hash: manualTxHash.trim(),
        amount: Number(manualAmount),
        asset: manualAsset,
        note: manualNote,
      });
      toast.success(t('wallet.depositSubmitted'));
      setManualTxHash(''); setManualAmount(''); setManualNote('');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('wallet.depositFailed'));
    } finally {
      setManualLoading(false);
    }
  };

  // Support ticket
  const handleSupportSubmit = async () => {
    if (!supportTxHash && !supportSenderAddr && !supportMessage) {
      toast.error(t('wallet.enterAtLeastOne'));
      return;
    }
    setSupportLoading(true);
    try {
      await depositAPI.submitSupport({
        tx_hash: supportTxHash.trim(),
        sender_address: supportSenderAddr.trim(),
        amount: Number(supportAmount) || 0,
        asset: supportAsset,
        message: supportMessage,
      });
      toast.success(t('wallet.supportSubmitted'));
      setSupportTxHash(''); setSupportSenderAddr(''); setSupportAmount(''); setSupportMessage('');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSupportLoading(false);
    }
  };

  const handleRecheck = async (depositId) => {
    setRecheckingId(depositId);
    try {
      const res = await depositAPI.recheck(depositId);
      if (res.data.verified) {
        toast.success(t('wallet.txVerified'));
      } else {
        toast.info(t('wallet.txNotVerifiedYet'));
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recheck failed');
    } finally {
      setRecheckingId(null);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || Number(withdrawAmount) <= 0) { toast.error(t('wallet.validAmount')); return; }
    if (!withdrawAddress) { toast.error(t('wallet.enterAddress')); return; }
    setWithdrawLoading(true);
    try {
      const res = await walletAPI.requestWithdraw({ asset: withdrawAsset, amount: Number(withdrawAmount), to_address: withdrawAddress });
      toast.success(`${t('wallet.withdrawRequestCreated')} ${res.data.fee} ${withdrawAsset.toUpperCase()}`);
      setWithdrawAmount(''); setWithdrawAddress('');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('wallet.withdrawFailed'));
    } finally {
      setWithdrawLoading(false);
    }
  };

  const canWithdraw = eligibility?.can_withdraw || false;
  const withdrawFee = withdrawAsset === 'usdt' ? (eligibility?.withdraw_fee_usdt || 2) : (eligibility?.withdraw_fee_trx || 1);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(label || t('wallet.addressCopied'));
  };

  const txTypeLabel = (type) => {
    const map = { bonus: t('wallet.filterBonus'), deposit: t('wallet.filterDeposit'), withdraw: t('wallet.filterWithdraw'), trade: t('wallet.filterTrade'), fee: t('wallet.filterFee'), referral_bonus_inviter: t('wallet.filterReferral'), referral_bonus_invited: t('wallet.filterReferral') };
    return map[type] || type;
  };

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-[rgba(247,200,75,0.12)] text-[#F7C84B] border-[#F7C84B]/20',
      confirmed: 'bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20',
      failed: 'bg-[rgba(217,74,74,0.12)] text-[#D94A4A] border-[#D94A4A]/20',
      rejected: 'bg-[rgba(217,74,74,0.12)] text-[#D94A4A] border-[#D94A4A]/20',
      support_review: 'bg-[rgba(40,170,255,0.12)] text-[#28AAFF] border-[#28AAFF]/20',
      unmatched: 'bg-[rgba(247,200,75,0.12)] text-[#F7C84B] border-[#F7C84B]/20',
      open: 'bg-[rgba(40,170,255,0.12)] text-[#28AAFF] border-[#28AAFF]/20',
      resolved: 'bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20',
    };
    const labels = {
      pending: t('wallet.depositPending'), confirmed: t('wallet.depositConfirmed'),
      failed: t('wallet.depositFailed2'), rejected: t('wallet.depositRejected'),
      support_review: t('wallet.supportTab'), unmatched: t('admin.unmatchedDeposits'),
      open: t('wallet.ticketOpen'), resolved: t('wallet.ticketResolved'),
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (<DashboardLayout title={t('wallet.title')}><div className="space-y-6"><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" /></div></DashboardLayout>);
  }

  return (
    <DashboardLayout title={t('wallet.title')}>
      <div className="space-y-6">
        {/* Balances */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-3">
                <Wallet className="w-4 h-4" /><span className="text-sm">{t('wallet.usdtBalance')}</span>
              </div>
              <div className="text-3xl font-semibold font-['Space_Grotesk'] tabular-nums">${wallet?.usdt_balance?.toFixed(2) || '0.00'}</div>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-[hsl(43,90%,60%)]">{t('admin.demoBalance')}: ${(wallet?.demo_usdt_balance ?? wallet?.usdt_balance ?? 0).toFixed(2)}</span>
                <span className="text-[#2DD36F]">{t('admin.realBalance')}: ${(wallet?.real_usdt_balance ?? 0).toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-3">
                <Wallet className="w-4 h-4" /><span className="text-sm">{t('wallet.trxBalance')}</span>
              </div>
              <div className="text-3xl font-semibold font-['Space_Grotesk'] tabular-nums">{wallet?.trx_balance?.toFixed(2) || '0.00'} TRX</div>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-[hsl(43,90%,60%)]">{t('admin.demoBalance')}: {(wallet?.demo_trx_balance ?? wallet?.trx_balance ?? 0).toFixed(2)}</span>
                <span className="text-[#2DD36F]">{t('admin.realBalance')}: {(wallet?.real_trx_balance ?? 0).toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Withdrawal Readiness */}
        <Card data-testid="wallet-withdrawal-readiness" className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                <Coins className="w-5 h-5 text-[hsl(var(--primary))]" />{t('wallet.withdrawalReadiness')}
              </CardTitle>
              {canWithdraw ? (
                <Badge className="bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20"><CheckCircle2 className="w-3 h-3 mr-1" /> {t('wallet.ready')}</Badge>
              ) : (
                <Badge className="bg-[rgba(247,200,75,0.12)] text-[#F7C84B] border-[#F7C84B]/20"><Clock className="w-3 h-3 mr-1" /> {t('wallet.completeSteps')}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {eligibility?.steps?.map((step, i) => {
                const isCompleted = step.status === 'completed';
                const isReady = step.status === 'ready';
                const isInProgress = step.status === 'in_progress';
                return (
                  <div key={i} className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCompleted || isReady ? 'bg-[rgba(45,211,111,0.15)]' : isInProgress ? 'bg-[rgba(247,200,75,0.15)]' : 'bg-[hsl(var(--secondary))]'}`}>
                      {isCompleted ? <CheckCircle2 className="w-5 h-5 text-[#2DD36F]" />
                      : isReady ? <ArrowUpToLine className="w-5 h-5 text-[#2DD36F]" />
                      : isInProgress ? <Clock className="w-5 h-5 text-[#F7C84B]" />
                      : <span className="text-sm font-semibold text-[hsl(var(--muted-foreground))]">{step.step}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className={`font-medium ${isCompleted || isReady ? 'text-[#2DD36F]' : ''}`}>{step.title_key ? t(step.title_key) : step.title}</h4>
                        {step.step === 2 && !isCompleted && (
                          <Button size="sm" onClick={() => setDepositModalOpen(true)} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-7 text-xs">
                            {step.title_key ? t(step.title_key) : step.title} <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                        {step.step === 3 && step.status === 'pending' && (
                          <Button size="sm" onClick={() => navigate('/kyc')} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-7 text-xs">{t('wallet.kycStartBtn')} <ArrowRight className="w-3 h-3 ml-1" /></Button>
                        )}
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">{step.desc_key ? t(step.desc_key) : step.description}</p>
                      {step.progress !== undefined && !isCompleted && <Progress value={step.progress} className="h-1.5 mt-2" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs defaultValue="auto">
          <TabsList className="bg-[hsl(var(--secondary))] flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="auto" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm">
              <Zap className="w-4 h-4" /> {t('wallet.autoDetectTitle')}
            </TabsTrigger>
            <TabsTrigger value="manual" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm">
              <FileText className="w-4 h-4" /> {t('wallet.manualTab')}
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm">
              <MessageSquarePlus className="w-4 h-4" /> {t('wallet.supportTab')}
            </TabsTrigger>
            <TabsTrigger id="deposit-tab" value="demo" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm">
              <ArrowDownToLine className="w-4 h-4" /> {t('wallet.simTab')}
            </TabsTrigger>
            <TabsTrigger value="withdraw" className="data-[state=active]:bg-[hsl(var(--card))] gap-1.5 text-xs sm:text-sm">
              <ArrowUpToLine className="w-4 h-4" /> {t('wallet.withdrawTab')}
            </TabsTrigger>
          </TabsList>

          {/* ===== AUTO DEPOSIT TAB ===== */}
          <TabsContent value="auto">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[hsl(var(--primary))]" />{t('wallet.autoDetectTitle')}
                </CardTitle>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('wallet.autoDetectDesc')}</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Deposit Button */}
                <Button
                  data-testid="open-deposit-modal-btn"
                  onClick={() => setDepositModalOpen(true)}
                  className="w-full h-14 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--primary))]/80 text-[hsl(var(--primary-foreground))] hover:opacity-90 text-base font-semibold gap-3 rounded-xl"
                >
                  <Coins className="w-6 h-6" />
                  {t('wallet.depositCrypto')}
                  <ArrowRight className="w-5 h-5" />
                </Button>

                {/* Buy with Card Button */}
                <Button
                  data-testid="open-card-purchase-btn"
                  onClick={() => setCardPurchaseOpen(true)}
                  variant="outline"
                  className="w-full h-14 border-[#F7931A]/30 text-[#F7931A] hover:bg-[rgba(247,147,26,0.05)] hover:border-[#F7931A]/50 text-base font-semibold gap-3 rounded-xl"
                >
                  <CreditCard className="w-6 h-6" />
                  {t('gamefuel.buyWithCard')}
                  {gamefuelStatus?.campaign_enabled && (
                    <Badge className="bg-[rgba(247,147,26,0.15)] text-[#F7931A] border-[#F7931A]/20 text-[10px] ml-1">
                      <Zap className="w-3 h-3 mr-0.5" /> GameFuel
                    </Badge>
                  )}
                </Button>

                {/* Recent Deposits */}
                {myDeposits.length > 0 && (
                  <div className="border-t border-[hsl(var(--border))] pt-6">
                    <h3 className="text-base font-semibold mb-4 font-['Space_Grotesk']">{t('wallet.myDeposits')}</h3>
                    <div className="space-y-3">
                      {myDeposits.map((dep) => (
                        <div key={dep.id} data-testid={`deposit-item-${dep.id}`} className="flex items-center justify-between p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              {statusBadge(dep.status)}
                              <span className="text-sm font-medium tabular-nums">
                                {dep.verified_amount ?? dep.declared_amount} {(dep.verified_asset || dep.declared_asset || '').toUpperCase()}
                              </span>
                              {dep.source === 'auto_scan' && <Badge className="bg-[rgba(18,200,178,0.12)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 text-[10px]">Auto</Badge>}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                              <span className="font-mono truncate max-w-[200px]">{dep.tx_hash}</span>
                              {dep.tx_hash && (
                                <>
                                  <button onClick={() => copyText(dep.tx_hash, t('common.copied'))} className="hover:text-[hsl(var(--foreground))] transition-colors"><Copy className="w-3 h-3" /></button>
                                  <a href={`https://tronscan.org/#/transaction/${dep.tx_hash}`} target="_blank" rel="noopener noreferrer" className="hover:text-[hsl(var(--primary))] transition-colors"><ExternalLink className="w-3 h-3" /></a>
                                </>
                              )}
                            </div>
                            <span className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(dep.created_at).toLocaleString(lang)}</span>
                          </div>
                          {(dep.status === 'pending' || dep.status === 'failed') && (
                            <Button size="sm" variant="ghost" data-testid={`recheck-deposit-${dep.id}`} disabled={recheckingId === dep.id} onClick={() => handleRecheck(dep.id)} className="text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10">
                              <RefreshCw className={`w-4 h-4 mr-1 ${recheckingId === dep.id ? 'animate-spin' : ''}`} />{t('wallet.recheckBtn')}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== MANUAL TX SUBMIT TAB ===== */}
          <TabsContent value="manual">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[hsl(var(--primary))]" />{t('wallet.manualTab')}
                </CardTitle>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('wallet.onchainDepositDesc')}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('wallet.assetLabel')}</Label>
                    <Select value={manualAsset} onValueChange={setManualAsset}>
                      <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                        <SelectItem value="usdt">USDT (TRC20)</SelectItem>
                        <SelectItem value="trx">TRX</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('wallet.declaredAmount')}</Label>
                    <Input type="number" data-testid="manual-deposit-amount" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} placeholder="0.00" className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('wallet.txHash')}</Label>
                  <Input data-testid="manual-deposit-txhash" value={manualTxHash} onChange={(e) => setManualTxHash(e.target.value)} placeholder={t('wallet.txHashPlaceholder')} className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono text-sm" />
                </div>
                <div className="space-y-2">
                  <Label>{t('wallet.depositNote')}</Label>
                  <Input data-testid="manual-deposit-note" value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder={t('wallet.depositNote')} className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
                </div>
                <Button data-testid="manual-deposit-submit" onClick={handleManualSubmit} disabled={manualLoading || !manualTxHash || !manualAmount} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-11">
                  {manualLoading ? t('common.processing') : t('wallet.submitDeposit')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== SUPPORT TAB ===== */}
          <TabsContent value="support">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                  <MessageSquarePlus className="w-5 h-5 text-[#28AAFF]" />{t('wallet.supportTitle')}
                </CardTitle>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('wallet.supportDesc')}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('wallet.assetLabel')}</Label>
                    <Select value={supportAsset} onValueChange={setSupportAsset}>
                      <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                        <SelectItem value="usdt">USDT (TRC20)</SelectItem>
                        <SelectItem value="trx">TRX</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('wallet.declaredAmount')}</Label>
                    <Input type="number" data-testid="support-amount" value={supportAmount} onChange={(e) => setSupportAmount(e.target.value)} placeholder="0.00" className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('wallet.txHash')} ({t('wallet.ifAvailable')})</Label>
                  <Input data-testid="support-txhash" value={supportTxHash} onChange={(e) => setSupportTxHash(e.target.value)} placeholder={t('wallet.txHashPlaceholder')} className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono text-sm" />
                </div>
                <div className="space-y-2">
                  <Label>{t('wallet.supportSenderAddr')}</Label>
                  <Input data-testid="support-sender-addr" value={supportSenderAddr} onChange={(e) => setSupportSenderAddr(e.target.value)} placeholder={t('wallet.supportSenderPlaceholder')} className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono text-sm" />
                </div>
                <div className="space-y-2">
                  <Label>{t('wallet.supportMessage')}</Label>
                  <Textarea data-testid="support-message" value={supportMessage} onChange={(e) => setSupportMessage(e.target.value)} placeholder={t('wallet.supportMessagePlaceholder')} rows={3} className="bg-[hsl(var(--background))] border-[hsl(var(--border))] resize-none" />
                </div>
                <Button data-testid="support-submit" onClick={handleSupportSubmit} disabled={supportLoading || (!supportTxHash && !supportSenderAddr && !supportMessage)} className="w-full bg-[#28AAFF] text-white hover:bg-[#28AAFF]/90 h-11 gap-2">
                  <Send className="w-4 h-4" />
                  {supportLoading ? t('common.processing') : t('wallet.supportSubmit')}
                </Button>

                {/* My Support Tickets */}
                {myTickets.length > 0 && (
                  <div className="border-t border-[hsl(var(--border))] pt-6">
                    <h3 className="text-base font-semibold mb-4 font-['Space_Grotesk']">{t('wallet.myTickets')}</h3>
                    <div className="space-y-3">
                      {myTickets.map((ticket) => (
                        <div key={ticket.id} data-testid={`support-ticket-${ticket.id}`} className="p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                          <div className="flex items-center gap-3 mb-2">
                            {statusBadge(ticket.status)}
                            {ticket.declared_amount > 0 && <span className="text-sm font-medium tabular-nums">{ticket.declared_amount} {ticket.declared_asset?.toUpperCase()}</span>}
                          </div>
                          {ticket.tx_hash && <p className="text-xs text-[hsl(var(--muted-foreground))] font-mono truncate">TX: {ticket.tx_hash}</p>}
                          {ticket.message && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{ticket.message}</p>}
                          {ticket.admin_response && (
                            <div className="mt-2 p-2 rounded bg-[hsl(var(--background))] border border-[hsl(var(--border))]">
                              <p className="text-xs font-medium text-[hsl(var(--primary))]">Admin:</p>
                              <p className="text-sm">{ticket.admin_response}</p>
                            </div>
                          )}
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">{new Date(ticket.created_at).toLocaleString(lang)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== DEMO DEPOSIT TAB ===== */}
          <TabsContent value="demo">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader><CardTitle className="font-['Space_Grotesk']">{t('wallet.depositTitle')}</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>{t('wallet.depositAddress')}</Label>
                  <div className="flex items-center gap-2">
                    <code data-testid="wallet-deposit-address" className="font-mono text-xs bg-[hsl(var(--secondary))] px-3 py-2 rounded-lg flex-1 truncate">{wallet?.deposit_address_trx}</code>
                    <Button size="sm" variant="ghost" data-testid="wallet-copy-address-button" onClick={() => copyText(wallet?.deposit_address_trx)}><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
                {!wallet?.trx_gate_ok && (
                  <Alert className="bg-[rgba(18,200,178,0.08)] border-[hsl(var(--primary))]/20">
                    <Info className="w-4 h-4 text-[hsl(var(--primary))]" />
                    <AlertDescription className="text-sm"><strong>{t('wallet.hint')}:</strong> {t('wallet.depositHint')} {wallet?.total_trx_deposited?.toFixed(1) || 0} / 20 TRX</AlertDescription>
                  </Alert>
                )}
                <div className="border-t border-[hsl(var(--border))] pt-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{t('wallet.assetLabel')}</Label>
                      <Select value={depositAsset} onValueChange={setDepositAsset}>
                        <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]"><SelectItem value="trx">TRX</SelectItem><SelectItem value="usdt">USDT</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('wallet.amountLabel')}</Label>
                      <Input type="number" data-testid="wallet-deposit-amount-input" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="0.00" className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
                    </div>
                    <div className="flex items-end">
                      <Button data-testid="wallet-deposit-submit-button" onClick={handleDeposit} disabled={depositLoading} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90">{depositLoading ? t('common.processing') : t('wallet.depositSubmit')}</Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== WITHDRAW TAB ===== */}
          <TabsContent value="withdraw">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader><CardTitle className="font-['Space_Grotesk']">{t('wallet.withdrawTitle')}</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                {!canWithdraw ? (
                  <Alert className="bg-[rgba(247,200,75,0.08)] border-[#F7C84B]/20">
                    <ShieldCheck className="w-4 h-4 text-[#F7C84B]" />
                    <AlertDescription>
                      <p className="font-medium mb-2">{t('wallet.withdrawAlert')}</p>
                      <div className="space-y-1">
                        {!eligibility?.trx_gate_ok && <div className="flex items-center gap-2 text-sm"><XCircle className="w-4 h-4 text-[#D94A4A]" /><span>{t('wallet.withdrawMinTrx')} ({eligibility?.total_trx_deposited?.toFixed(1) || 0}/20)</span></div>}
                        {eligibility?.kyc_status !== 'approved' && <div className="flex items-center gap-2 text-sm"><XCircle className="w-4 h-4 text-[#D94A4A]" /><span>{t('wallet.withdrawKyc')}</span></div>}
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="bg-[rgba(45,211,111,0.08)] border-[#2DD36F]/20"><CheckCircle2 className="w-4 h-4 text-[#2DD36F]" /><AlertDescription><p className="font-medium">{t('wallet.withdrawReady')}</p></AlertDescription></Alert>
                )}
                <div className="grid grid-cols-1 gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('wallet.assetLabel')}</Label>
                      <Select value={withdrawAsset} onValueChange={setWithdrawAsset}>
                        <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]"><SelectItem value="usdt">USDT</SelectItem><SelectItem value="trx">TRX</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('wallet.amountLabel')}</Label>
                      <Input type="number" data-testid="wallet-withdraw-amount-input" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} placeholder="0.00" disabled={!canWithdraw} className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('wallet.withdrawAddress')}</Label>
                    <Input data-testid="wallet-withdraw-address-input" value={withdrawAddress} onChange={(e) => setWithdrawAddress(e.target.value)} placeholder="T..." disabled={!canWithdraw} className="bg-[hsl(var(--background))] border-[hsl(var(--border))] font-mono" />
                  </div>
                  {withdrawAmount && Number(withdrawAmount) > 0 && canWithdraw && (
                    <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                      <CardContent className="p-4 space-y-2">
                        <div className="flex justify-between text-sm"><span className="text-[hsl(var(--muted-foreground))]">{t('wallet.withdrawAmount')}</span><span className="tabular-nums">{Number(withdrawAmount).toFixed(2)} {withdrawAsset.toUpperCase()}</span></div>
                        <div className="flex justify-between text-sm"><span className="text-[hsl(var(--muted-foreground))]">{t('common.fee')}</span><span className="tabular-nums text-[#F7C84B]">-{withdrawFee} {withdrawAsset.toUpperCase()}</span></div>
                        <div className="border-t border-[hsl(var(--border))] pt-2 flex justify-between font-semibold"><span>{t('wallet.withdrawNet')}</span><span className="tabular-nums text-[#2DD36F]">{Math.max(0, Number(withdrawAmount) - withdrawFee).toFixed(2)} {withdrawAsset.toUpperCase()}</span></div>
                      </CardContent>
                    </Card>
                  )}
                  <Button data-testid="wallet-withdraw-submit-button" onClick={handleWithdraw} disabled={!canWithdraw || withdrawLoading || !withdrawAmount || !withdrawAddress} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-11">{withdrawLoading ? t('common.processing') : t('wallet.withdrawSubmit')}</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Transaction History */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-['Space_Grotesk']">{t('wallet.transactionHistory')}</CardTitle>
            <Select value={txFilter} onValueChange={setTxFilter}>
              <SelectTrigger className="w-[140px] bg-[hsl(var(--background))] border-[hsl(var(--border))]"><SelectValue placeholder={t('wallet.filterAll')} /></SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                <SelectItem value="all">{t('wallet.filterAll')}</SelectItem><SelectItem value="deposit">{t('wallet.filterDeposit')}</SelectItem><SelectItem value="withdraw">{t('wallet.filterWithdraw')}</SelectItem><SelectItem value="trade">{t('wallet.filterTrade')}</SelectItem><SelectItem value="bonus">{t('wallet.filterBonus')}</SelectItem><SelectItem value="referral_bonus_inviter">{t('wallet.filterReferral')}</SelectItem><SelectItem value="fee">{t('wallet.filterFee')}</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">{t('wallet.noTransactions')}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[hsl(var(--border))]">
                    <TableHead>{t('dashboard.txType')}</TableHead><TableHead>{t('common.description')}</TableHead><TableHead>{t('common.asset')}</TableHead><TableHead className="text-right">{t('common.amount')}</TableHead><TableHead className="text-right">{t('common.date')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id} className="border-[hsl(var(--border))]">
                      <TableCell>
                        <Badge className={
                          tx.entry_type === 'bonus' ? 'bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20'
                          : tx.entry_type === 'deposit' ? 'bg-[rgba(18,200,178,0.12)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20'
                          : tx.entry_type === 'withdraw' ? 'bg-[rgba(217,74,74,0.12)] text-[#D94A4A] border-[#D94A4A]/20'
                          : tx.entry_type === 'trade' ? 'bg-[rgba(23,185,230,0.12)] text-[hsl(var(--accent))] border-[hsl(var(--accent))]/20'
                          : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
                        }>{txTypeLabel(tx.entry_type)}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-[hsl(var(--muted-foreground))] max-w-[200px] truncate">{tx.description}</TableCell>
                      <TableCell className="uppercase font-medium">{tx.asset}</TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${tx.amount >= 0 ? 'text-[#2DD36F]' : 'text-[#D94A4A]'}`}>{tx.amount >= 0 ? '+' : ''}{tx.amount?.toFixed(2)}</TableCell>
                      <TableCell className="text-right text-[hsl(var(--muted-foreground))] text-sm">{new Date(tx.created_at).toLocaleDateString(lang)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Deposit Modal */}
        <DepositModal
          open={depositModalOpen}
          onOpenChange={setDepositModalOpen}
          onSuccess={loadData}
        />

        {/* Card Purchase Modal */}
        <CardPurchaseModal
          open={cardPurchaseOpen}
          onClose={() => setCardPurchaseOpen(false)}
          onSuccess={loadData}
        />
      </div>
    </DashboardLayout>
  );
}
